import { FormEvent, useEffect, useRef, useState } from "react";
import IntlTelInput, { CountryData } from "react-intl-tel-input-18";
import "react-intl-tel-input-18/dist/main.css";
import styled from "@emotion/styled";
import {
  getExample,
  parsePhoneNumber,
  getCountryCodeForRegionCode,
} from "awesome-phonenumber";
import { colors as themeColors } from "src/theme";
import { InputLabel } from "../typography";
import { ValidationErrorStyle } from "src/common";

const getCleanNumber = (value: string) => value.replace(/\D/g, "");

function formatNumberWithMask(
  inputNumber: string,
  mask: string,
  limitToMasked = true
): string {
  const cleanNumber = getCleanNumber(inputNumber);
  let cleanNumberIndex = 0;
  const masked = mask.split("").reduce((acc, curr) => {
    if (cleanNumberIndex >= cleanNumber.length) {
      return acc;
    }
    if (curr === "x") {
      const number = cleanNumber[cleanNumberIndex++];
      return `${acc}${number}`;
    }
    return `${acc}${curr}`;
  }, "");
  const restOfCleanNumber = cleanNumber.substring(cleanNumberIndex);
  return limitToMasked ? masked : masked + restOfCleanNumber;
}

const getMaskFromRegion = (regionCode: string) => {
  const formated = getExample(regionCode, "mobile").number?.national;
  if (!formated) {
    return "xxx xxx xxxx";
  }

  return formated.replace(/[0-9]/g, "x");
};

const formatNumber = (value: string, regionCode: string) => {
  const mask = getMaskFromRegion(regionCode);
  const cleanNumber = getCleanNumber(value);
  const maskValue =
    cleanNumber[0] !== "0" && regionCode.toLowerCase() === "hr" ? mask.substring(1) : mask;

  return formatNumberWithMask(value, maskValue);
};

type ContainerProps = {
  hasError: boolean;
};

const Container = styled.div<ContainerProps>`
  .intl-tel-input.allow-dropdown.separate-dial-code {
    width: 100%;
    color: ${themeColors.text1};
    font-family: "Red Hat Text", sans-serif;

    .flag-container {
      &:hover .selected-flag {
        background-color: rgba(0, 0, 0, 0.05);
      }

      .selected-flag {
        z-index: 1;
        position: relative;
        display: flex;
        height: 100%;
        padding: 0 6px 0 12px;
        width: fit-content;
        background: none;
        border-right: 1px solid ${themeColors.border5};
      }

      .selected-dial-code {
        padding: 0;
        margin-left: 6px;
        font-family: "Red Hat Text", sans-serif;
        font-size: 18px;
        font-weight: 400;
        line-height: 24px;
      }

      .country-list {
        z-index: 5;
      }
    }

    input {
      box-sizing: border-box;
      display: block;
      width: 100%;
      height: 44px;
      padding: 9px 12px;
      padding-right: 12px !important;
      padding-left: 107px !important;
      font-family: "Red Hat Text", sans-serif;
      font-size: 18px;
      font-weight: 400;
      font-style: normal;
      line-height: 24px;
      color: #4D4D4D;
      outline: none;
      background-color: #FFF;
      border-radius: 8px;
      border: 1px solid;
      border-color: ${({ hasError }) => (hasError ? themeColors.error1 : themeColors.border5)};
      transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
  
      &:focus {
        border-color: #666;
      }
  
      &[disabled] {
        border: 1px solid ${themeColors.border6},
        color: ${themeColors.text4},
        pointerEvents: none
      }
    }
  }
`;

export type InputPhoneNumberProps = {
  id?: string;
  label?: string;
  value?: string;
  error?: string;
  onChange?: (value: { phoneNumber: string, countryCode: string, cleanPhoneNumber: string }) => void;
  disabled?: boolean;
};

export const getRegionCodeAndNumber = (
  value: string,
  currentRegionCode: string
): { region: string; number: string } => {
  const startsWithPlus = value[0] === "+";
  if (!startsWithPlus) {
    return { region: currentRegionCode, number: getCleanNumber(value) };
  }
  try {
    const parsed = parsePhoneNumber(value);
    const regionCode = parsed.regionCode || currentRegionCode;
    const countryCode = getCountryCodeForRegionCode(regionCode);
    const number = getCleanNumber(value.replace(countryCode.toString(), ""));

    return {
      region: regionCode === "ZZ" ? currentRegionCode : regionCode,
      number: number.toString(),
    };
  } catch (_) {
    const regionCode = `+${getCountryCodeForRegionCode(currentRegionCode)}`;

    return {
      region: currentRegionCode,
      number: getCleanNumber(value.replace(regionCode, "")),
    };
  }
};

const DEFAULT_REGION_CODE = "hr";

export const InputPhoneNumber: React.FC<InputPhoneNumberProps> = ({
  id,
  label,
  error = '',
  value = '',
  onChange,
  disabled
}) => {
  const [rawNumber, setRawNumber] = useState("");
  const [regionCode, setRegionCode] = useState(DEFAULT_REGION_CODE);

  useEffect(() => {
    if (!value) {
      return;
    }
    const { region, number } = getRegionCodeAndNumber(value, regionCode);
    if (number !== rawNumber) {
      setRawNumber(number);
    }
    if (region !== regionCode) {
      setRegionCode(region);
    }
  }, [value]);

  const onFlagSelect = (_: string, selectedCountryData: CountryData) => {
    const regCode = selectedCountryData.iso2 || DEFAULT_REGION_CODE;
    setRegionCode(regCode);
    const newValue = `+${selectedCountryData.dialCode}${rawNumber}`;
    if (newValue === value) {
      return;
    }
    setRawNumber("");
    // const dialCode = selectedCountryData.dialCode || "";
    if (onChange) onChange({ phoneNumber: newValue, countryCode: regCode, cleanPhoneNumber: rawNumber });
  };

  const isNumeric = (v: string) => /\d/.test(v);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const componentRef = useRef<IntlTelInput | null>(null);

  // synching internal flag with regionCode prop
  useEffect(() => {
    if (!componentRef.current) {
      return;
    }
    const setFlag = (regCode: string) => {
      if (!componentRef.current) {
        return;
      }
      componentRef.current.setFlag(regCode.toLowerCase());
    };
    const componentCode = componentRef.current.state.countryCode;
    if (componentCode.toLocaleLowerCase() !== regionCode.toLocaleLowerCase()) {
      setFlag(regionCode);
    }
  }, [componentRef.current?.state.countryCode, regionCode]);

  const getNewSelectionStart = (
    selectionStart: number,
    val: string
  ): number => {
    const letterBefore = val[selectionStart - 1];
    const letterAfter = val[selectionStart];

    if (
      letterAfter === undefined ||
      letterBefore === undefined ||
      (isNumeric(letterBefore) && isNumeric(letterAfter))
    ) {
      return selectionStart;
    }

    return getNewSelectionStart(selectionStart + 1, val);
  };

  // updating carret position after formating
  useEffect(() => {
    const selectionStart = inputRef.current?.selectionStart;
    if (!inputRef.current || !selectionStart) {
      return;
    }
    const formatedValue = formatNumber(rawNumber, regionCode);
    const newSelectionStart = getNewSelectionStart(
      selectionStart,
      formatedValue
    );
    if (newSelectionStart !== selectionStart) {
      inputRef.current.setSelectionRange(newSelectionStart, newSelectionStart);
    }
  }, [rawNumber]);

  const onRawInput = (event: FormEvent<HTMLInputElement>) => {
    const { value: val } = event.currentTarget;
    inputRef.current = event.currentTarget;
    const cleanNumber = getCleanNumber(formatNumber(val, regionCode));
    setRawNumber(cleanNumber);
    const countryCode = getCountryCodeForRegionCode(regionCode);
    if (onChange) {
      if (!cleanNumber) {
        onChange({ phoneNumber: '', countryCode: countryCode.toString(), cleanPhoneNumber: '' });
      } else {
        onChange({ 
          phoneNumber: `+${countryCode}${cleanNumber}`, 
          countryCode: countryCode.toString(),
          cleanPhoneNumber: cleanNumber
        });
      }
    }
  };

  return (
    <Container hasError={!!error}>
      {label ? <InputLabel htmlFor={id} disabled={disabled}>{label}</InputLabel> : null}
      <IntlTelInput
        autoComplete="off"
        telInputProps={{ onInput: onRawInput }}
        value={formatNumber(rawNumber, regionCode)}
        containerClassName="intl-tel-input"
        preferredCountries={["hr", "si", "de", "it"]}
        // onPhoneNumberBlur={() => {
        //   setFocus(false);
        //   if (required && !rawNumber) {
        //     setErrorState(true);
        //     setErrorText(error || 'Unesi ispravni broj telefona');
        //   }
        // }}
        // onPhoneNumberFocus={() => setFocus(true)}
        onSelectFlag={onFlagSelect}
        separateDialCode={true}
        ref={componentRef}
        disabled={disabled}
        placeholder=''
      />
      {error ? <ValidationErrorStyle>{error}</ValidationErrorStyle> : null}
    </Container>
  );
};

export const getFullPhoneNumberFromInput = (value: string): string | null => {
  const parsed = parsePhoneNumber(value);
  return parsed.number?.e164 || null
}
